import React, { useState } from "react";
import { useI18n } from "../../lib/i18n";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const KEYBOARD_ACTIONS = [
  "move_up", "move_down", "move_left", "move_right",
  "jump", "attack", "throw", "ability", "secondary", "interact", "inventory", "pause",
];

const GAMEPAD_ACTIONS = [
  "move", "camera", "jump", "attack", "secondary", "interact", "inventory", "pause",
];

const XBOX_BUTTONS = ["A", "B", "X", "Y", "LB", "RB", "LT", "RT", "Back", "Start", "LeftStick", "RightStick", "DPadUp", "DPadDown", "DPadLeft", "DPadRight"];
const PS_BUTTONS = ["Cross", "Circle", "Square", "Triangle", "L1", "R1", "L2", "R2", "Share", "Options", "LeftStick", "RightStick", "DPadUp", "DPadDown", "DPadLeft", "DPadRight"];
const SWITCH_BUTTONS = ["A", "B", "X", "Y", "L", "R", "ZL", "ZR", "Minus", "Plus", "LeftStick", "RightStick", "DPadUp", "DPadDown", "DPadLeft", "DPadRight"];

function KeyCap({ k, action, onClick, active }) {
  const isLong = k && (k.length > 3);
  return (
    <button
      data-testid={`keycap-${k || "empty"}`}
      onClick={onClick}
      className={
        "border transition-colors text-xs font-mono px-2 h-10 min-w-[40px] flex items-center justify-center " +
        (active
          ? "bg-cyan-400 text-zinc-950 border-cyan-400"
          : "bg-zinc-900 text-zinc-200 border-zinc-800 hover:border-cyan-500/60")
      }
      style={{ minWidth: isLong ? 60 : 40 }}
    >
      {k || "—"}
      {action && <span className="ml-2 text-[9px] tracking-[0.2em] uppercase text-cyan-300">{action}</span>}
    </button>
  );
}

const KEY_ROWS = [
  ["Escape", "1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "-"],
  ["Tab", "Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["CapsLock", "A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["Shift", "Z", "X", "C", "V", "B", "N", "M"],
  ["Ctrl", "Alt", "Space", "Enter"],
];

export default function ControlsPanel({ project, onChange }) {
  const { t } = useI18n();
  const controls = project.controls || {};
  const [section, setSection] = useState("keyboard");
  const [bindingFor, setBindingFor] = useState(null);

  const updateKeyboard = (action, value) => {
    onChange({ controls: { ...controls, keyboard: { ...controls.keyboard, [action]: value } } });
  };

  const updateGamepad = (action, value) => {
    onChange({ controls: { ...controls, gamepad: { ...controls.gamepad, [action]: value } } });
  };

  const updateGamepadType = (type) => {
    onChange({ controls: { ...controls, gamepad_type: type } });
  };

  const handleKeyClick = (k) => {
    if (bindingFor) {
      updateKeyboard(bindingFor, k);
      setBindingFor(null);
    }
  };

  const gamepadButtons =
    controls.gamepad_type === "playstation" ? PS_BUTTONS :
    controls.gamepad_type === "switch" ? SWITCH_BUTTONS : XBOX_BUTTONS;

  const findActionForKey = (key) => {
    const kb = controls.keyboard || {};
    return KEYBOARD_ACTIONS.find((a) => (kb[a] || "").toString() === key);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-1 mb-6 bg-zinc-900 border border-zinc-800 p-1 inline-flex">
        {[
          { id: "keyboard", l: t("controls.keyboard") },
          { id: "gamepad", l: t("controls.gamepad") },
        ].map((s) => (
          <button
            key={s.id}
            data-testid={`controls-section-${s.id}`}
            onClick={() => setSection(s.id)}
            className={
              "px-4 py-2 text-xs tracking-[0.25em] uppercase transition-colors " +
              (section === s.id ? "bg-cyan-400 text-zinc-950" : "text-zinc-400 hover:text-zinc-100")
            }
          >
            {s.l}
          </button>
        ))}
      </div>

      {section === "keyboard" && (
        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
            {KEYBOARD_ACTIONS.map((a) => (
              <div key={a} className="bg-zinc-950 px-4 py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">ACTION</div>
                  <div className="text-sm font-mono">{a}</div>
                </div>
                <button
                  data-testid={`bind-${a}`}
                  onClick={() => setBindingFor(bindingFor === a ? null : a)}
                  className={
                    "px-3 py-2 text-xs font-mono border transition-colors " +
                    (bindingFor === a
                      ? "border-cyan-400 text-cyan-300 gf-blink"
                      : "border-zinc-800 text-zinc-300 hover:border-cyan-500/60")
                  }
                >
                  {bindingFor === a ? "Press a key..." : (controls.keyboard?.[a] || "—")}
                </button>
              </div>
            ))}
          </div>

          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">VISUAL LAYOUT — click a key to bind</div>
            <div className="space-y-1 inline-block bg-zinc-900 border border-zinc-800 p-3">
              {KEY_ROWS.map((row, i) => (
                <div key={i} className="flex gap-1">
                  {row.map((k) => (
                    <KeyCap
                      key={k}
                      k={k}
                      action={findActionForKey(k)}
                      onClick={() => handleKeyClick(k)}
                      active={bindingFor && findActionForKey(k) === bindingFor}
                    />
                  ))}
                </div>
              ))}
              <div className="flex gap-1 mt-2 pt-2 border-t border-zinc-800">
                <KeyCap k="MouseLeft" action={findActionForKey("MouseLeft")} onClick={() => handleKeyClick("MouseLeft")} />
                <KeyCap k="MouseRight" action={findActionForKey("MouseRight")} onClick={() => handleKeyClick("MouseRight")} />
                <KeyCap k="MouseMiddle" action={findActionForKey("MouseMiddle")} onClick={() => handleKeyClick("MouseMiddle")} />
              </div>
            </div>
          </div>
        </div>
      )}

      {section === "gamepad" && (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">{t("controls.type")}</div>
            <Select value={controls.gamepad_type || "xbox"} onValueChange={updateGamepadType}>
              <SelectTrigger data-testid="gamepad-type-select" className="bg-zinc-900 border-zinc-800 rounded-none w-48 font-mono text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 rounded-none font-mono">
                <SelectItem value="xbox">Xbox</SelectItem>
                <SelectItem value="playstation">PlayStation</SelectItem>
                <SelectItem value="switch">Switch Pro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
            {GAMEPAD_ACTIONS.map((a) => (
              <div key={a} className="bg-zinc-950 px-4 py-3">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">{a}</div>
                <Select
                  value={controls.gamepad?.[a] || gamepadButtons[0]}
                  onValueChange={(v) => updateGamepad(a, v)}
                >
                  <SelectTrigger data-testid={`gamepad-bind-${a}`} className="bg-zinc-900 border-zinc-800 rounded-none font-mono text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 rounded-none font-mono max-h-72">
                    {gamepadButtons.map((b) => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          <div className="border border-zinc-800 p-6 bg-zinc-900">
            <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-400 mb-4">// VISUAL MAP — {controls.gamepad_type?.toUpperCase()}</div>
            <div className="relative w-full max-w-md mx-auto aspect-[16/10] bg-zinc-950 border border-zinc-800">
              {/* Left stick */}
              <div className="absolute left-[18%] top-[55%] -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-cyan-500/60 flex items-center justify-center text-[9px] tracking-[0.2em] uppercase text-cyan-300">LS</div>
              {/* DPad */}
              <div className="absolute left-[35%] top-[60%] -translate-x-1/2 -translate-y-1/2 grid grid-cols-3 gap-px">
                <div /><div className="w-5 h-5 bg-zinc-800" /><div />
                <div className="w-5 h-5 bg-zinc-800" /><div className="w-5 h-5 bg-zinc-900" /><div className="w-5 h-5 bg-zinc-800" />
                <div /><div className="w-5 h-5 bg-zinc-800" /><div />
              </div>
              {/* Right stick */}
              <div className="absolute right-[28%] top-[60%] translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-cyan-500/60 flex items-center justify-center text-[9px] tracking-[0.2em] uppercase text-cyan-300">RS</div>
              {/* Face buttons */}
              <div className="absolute right-[12%] top-[55%] translate-x-0 -translate-y-1/2 grid grid-cols-3 gap-px">
                <div /><div className="w-6 h-6 rounded-full bg-yellow-400 text-[10px] flex items-center justify-center text-zinc-950 font-bold">{gamepadButtons[3]?.[0]}</div><div />
                <div className="w-6 h-6 rounded-full bg-cyan-400 text-[10px] flex items-center justify-center text-zinc-950 font-bold">{gamepadButtons[2]?.[0]}</div><div /><div className="w-6 h-6 rounded-full bg-red-500 text-[10px] flex items-center justify-center text-zinc-950 font-bold">{gamepadButtons[1]?.[0]}</div>
                <div /><div className="w-6 h-6 rounded-full bg-emerald-400 text-[10px] flex items-center justify-center text-zinc-950 font-bold">{gamepadButtons[0]?.[0]}</div><div />
              </div>
              {/* Shoulders */}
              <div className="absolute left-[10%] top-[15%] text-[10px] tracking-[0.2em] uppercase text-zinc-400 border border-zinc-800 px-2 py-1">{gamepadButtons[4]}</div>
              <div className="absolute right-[10%] top-[15%] text-[10px] tracking-[0.2em] uppercase text-zinc-400 border border-zinc-800 px-2 py-1">{gamepadButtons[5]}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
